import { useState } from 'react';

const SUPPORT_LEVELS = [
  { amount: '$100', desc: 'A great way to show your support for local youth football.' },
  { amount: '$150', desc: 'A little more goes a long way for our kids on the field.' },
  { amount: '$200', desc: 'Make a meaningful impact for 300+ Caledonia youth athletes.' },
];

const PLACEHOLDER_SLOTS = Array.from({ length: 24 }, (_, i) => i + 1);

const COMMUNITY_WALL = [
  { name: 'Arctel, Corporation', slot: 1 },
  { name: 'PVCMillwork.com', slot: 2 },
  { name: 'Caledonia Pharmacy', slot: 3 },
];

export default function CommunitySupporters() {
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    supportLevel: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [activeLevel, setActiveLevel] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const handleLevelClick = (amount: string) => {
    setActiveLevel(amount);
    setFormData((prev) => ({ ...prev, supportLevel: amount }));
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('community-supporter-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams({
        businessName: formData.businessName,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        supportLevel: formData.supportLevel,
      }).toString();

      const res = await fetch('https://readdy.ai/api/form/d6ne1iaqoe30lj0v8mog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ businessName: '', contactName: '', email: '', phone: '', supportLevel: '' });
        setActiveLevel(null);
        setShowForm(false);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filledSlots = new Map(COMMUNITY_WALL.map((b) => [b.slot, b.name]));

  return (
    <section id="community-supporters" className="py-24 bg-white border-t-4 border-gray-100">
      <div className="max-w-5xl mx-auto px-6">

        {/* Header */}
        <div className="text-center mb-6">
          <span className="inline-block bg-yellow-100 text-yellow-800 text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full mb-5">
            Community Level
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-5">
            Community <span className="text-purple-700">Supporters</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-3">
            Not every business needs a full sponsorship package — and that's perfectly okay.
          </p>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed font-medium">
            Our Community Supporter level gives local businesses a simple way to support Caledonia Youth Football and the families in our community.
          </p>
        </div>

        <p className="text-center text-gray-500 font-semibold text-base mb-10 tracking-wide uppercase text-sm">
          Choose the level that feels right for your business
        </p>

        {/* Level Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {SUPPORT_LEVELS.map((level) => (
            <div
              key={level.amount}
              className={`relative bg-white border-2 rounded-2xl p-8 text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer ${
                activeLevel === level.amount
                  ? 'border-purple-700 shadow-xl ring-2 ring-purple-200'
                  : 'border-gray-200 shadow-md'
              }`}
              onClick={() => handleLevelClick(level.amount)}
            >
              {activeLevel === level.amount && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-700 text-white text-xs font-black px-3 py-1 rounded-full whitespace-nowrap">
                  Selected
                </div>
              )}
              <div className="text-5xl font-black text-purple-700 mb-3">{level.amount}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">{level.desc}</p>
              <button
                onClick={(e) => { e.stopPropagation(); handleLevelClick(level.amount); }}
                className={`w-full px-4 py-3 text-sm font-bold rounded-lg transition-all duration-200 cursor-pointer ${
                  activeLevel === level.amount
                    ? 'bg-purple-700 text-white hover:bg-purple-800'
                    : 'bg-gray-900 text-white hover:bg-purple-700'
                }`}
              >
                Become a Community Supporter
              </button>
            </div>
          ))}
        </div>

        {/* Benefits */}
        <div className="bg-gray-50 rounded-2xl p-8 mb-14 border border-gray-200">
          <h3 className="text-xl font-black text-gray-900 mb-5 text-center">Community Supporters Receive</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: 'ri-global-line', text: 'Recognition on the CYFA website' },
              { icon: 'ri-heart-line', text: 'A thank-you mention on CYFA social media' },
              { icon: 'ri-trophy-line', text: 'Listing on our CYFA Community Supporter Wall' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="w-9 h-9 flex items-center justify-center bg-purple-100 rounded-full flex-shrink-0">
                  <i className={`${item.icon} text-purple-700 text-base`}></i>
                </div>
                <span className="text-gray-700 font-medium text-sm leading-snug">{item.text}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 text-center border-t border-gray-200 pt-6">
            <p className="text-gray-700 font-semibold text-base">
              Every contribution helps support <span className="text-purple-700 font-black">300+ local kids</span> who take the field each season.
            </p>
            <p className="text-gray-500 text-sm mt-2 leading-relaxed max-w-xl mx-auto">
              And our CYFA families truly appreciate the businesses that step up to support the kids in our community.
            </p>
          </div>
        </div>

        {/* Inline Form */}
        {showForm && (
          <div id="community-supporter-form" className="bg-white border-2 border-purple-200 rounded-2xl shadow-xl p-8 mb-14 transition-all duration-300">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-black text-gray-900 mb-2">
                Join as a <span className="text-purple-700">{activeLevel} Community Supporter</span>
              </h3>
              <p className="text-gray-500 text-sm">Fill out the form below and we'll be in touch!</p>
            </div>

            {submitStatus === 'success' ? (
              <div className="bg-green-50 border-2 border-green-400 rounded-xl p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-green-100 rounded-full">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <p className="text-lg font-black text-green-800">Thank you for supporting CYFA!</p>
                <p className="text-green-700 text-sm mt-1">A CYFA representative will reach out to you soon.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">Business Name *</label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                      required
                      placeholder="Your Business Name"
                      className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:border-purple-700 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">Contact Name *</label>
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                      required
                      placeholder="Your Name"
                      className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:border-purple-700 focus:outline-none transition-colors"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:border-purple-700 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      placeholder="(555) 123-4567"
                      className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:border-purple-700 focus:outline-none transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-2">Support Level *</label>
                  <select
                    name="supportLevel"
                    value={formData.supportLevel}
                    onChange={(e) => setFormData({ ...formData, supportLevel: e.target.value })}
                    required
                    className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl focus:border-purple-700 focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Select a level</option>
                    <option value="$100">$100 Community Supporter</option>
                    <option value="$150">$150 Community Supporter</option>
                    <option value="$200">$200 Community Supporter</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-purple-700 hover:bg-purple-800 disabled:bg-gray-400 text-white text-sm font-black rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit My Support'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-bold rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-300 rounded-xl p-4 text-center">
                    <p className="text-sm font-bold text-red-700">Something went wrong. Please try again.</p>
                  </div>
                )}
              </form>
            )}
          </div>
        )}

        {/* Community Support Wall */}
        <div>
          <div className="text-center mb-8">
            <h3 className="text-3xl font-black text-gray-900 mb-3">
              Community <span className="text-purple-700">Support Wall</span>
            </h3>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              These local businesses have stepped up to support Caledonia youth athletes. Your business could be here too.
            </p>
          </div>

          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
            {PLACEHOLDER_SLOTS.map((slot) => {
              const name = filledSlots.get(slot);
              return (
                <div
                  key={slot}
                  className={`aspect-square rounded-xl flex items-center justify-center text-center p-2 transition-all duration-200 ${
                    name
                      ? 'bg-purple-700 text-white shadow-md'
                      : 'bg-gray-100 border-2 border-dashed border-gray-300 hover:border-purple-300 hover:bg-purple-50 cursor-pointer'
                  }`}
                  title={name ? name : 'Available — become a Community Supporter!'}
                  onClick={() => !name && handleLevelClick('$100')}
                >
                  {name ? (
                    <span className="text-[9px] sm:text-[10px] font-bold leading-tight break-words overflow-hidden w-full text-center line-clamp-4 hyphens-auto">{name}</span>
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-add-line text-gray-400 text-base"></i>
                      </div>
                      <span className="text-gray-400 text-xs font-medium leading-tight hidden sm:block">Your Biz</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-center text-gray-400 text-xs mt-4">
            {PLACEHOLDER_SLOTS.length - COMMUNITY_WALL.length} spots remaining — help us fill out the wall!
          </p>
        </div>
      </div>
    </section>
  );
}
